import React, { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';

import {
  Bucket,
  GetSalesInsightsAggregatedRequest,
  TendersGroupedAggregate,
  TendersGroupedAggregateCompare,
  useTendersGroupedAggregateCompare,
} from '../../../../api/indicators';
import { LineBarChartDataItem } from '../charts/lineBarChart/LineBarChart';

import { dateRange, formatDateParam } from '../../../../helpers/dateUtils';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { DashboardContext } from '../../DashboardContext';
import { SettingsContext } from '../../../../context/SettingsContext';

import DoubleBarChartCompared from '../charts/DoubleBarChart/DoubleBarChartCompared';
import type { DoubleBarChartDataItem } from '../charts/DoubleBarChart/DoubleBarChartCompared';
import { getBucketFromComparedRanges } from '../../dashboardHelpers';

const ComparedSalesByTender = () => {
  const { t } = useTranslation();
  const { calendarPreferences } = useContext(SettingsContext);
  const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
  const { companyConfig, refreshDate } = useContext(DashboardContext);

  const initialStartDate = filters.periodFrom
    ? filters.periodFrom
    : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

  const initialEndDate = filters.periodTo
    ? filters.periodTo
    : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

  const initialCompareStart = filters.comparePeriodFrom
    ? filters.comparePeriodFrom
    : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

  const initialCompareEnd = filters.comparePeriodTo
    ? filters.comparePeriodTo
    : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;

  const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
    fields: 'amount,volume',
    startDate: formatDateParam(initialStartDate),
    endDate: formatDateParam(initialEndDate),
    compareStartDate: formatDateParam(initialCompareStart),
    compareEndDate: formatDateParam(initialCompareEnd),
    locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
    locationArea: filters?.locationArea?.id,
    businessStartHour: calendarPreferences.daysStart,
    bucket: getBucketFromComparedRanges(
      initialStartDate,
      initialEndDate,
      initialCompareStart,
      initialCompareEnd
    )
  });

  const {
    data: groupedAggregateTendersCompare,
    isLoading,
    isError,
    refetch: fetchSalesAndTransactions
  } = useTendersGroupedAggregateCompare<TendersGroupedAggregateCompare>(params, 'tenders-grouped-aggregate-compare');

  const transformData = (dataItem: { dateTime: string } & TendersGroupedAggregate): DoubleBarChartDataItem => {
    const result: DoubleBarChartDataItem = {
      dateTime: new Date(dataItem.dateTime),
      volume: 0,
    };

    dataItem.tendersGrouped.forEach(({ key, fieldData }) => {
      result[key] = fieldData.amount ?? 0;
      result.volume += fieldData.volume ?? 0;
    });

    return result;
  };

  useEffect(() => {
    const start = filters.periodFrom
      ? filters.periodFrom
      : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

    const end = filters.periodTo
      ? filters.periodTo
      : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

    const compareStart = filters.comparePeriodFrom
      ? filters.comparePeriodFrom
      : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

    const compareEnd = filters.comparePeriodTo
      ? filters.comparePeriodTo
      : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;

    const bucket = getBucketFromComparedRanges(
      start,
      end,
      compareStart,
      compareEnd
    );
    setParams({
      ...params,
      startDate: formatDateParam(start),
      endDate: formatDateParam(end),
      compareStartDate: formatDateParam(compareStart),
      compareEndDate: formatDateParam(compareEnd),
      locations: filters?.locations?.join(','),
      locationArea: filters?.locationArea?.id,
      bucket
    });
  }, [filters]);

  useEffect(() => { fetchSalesAndTransactions(); }, [params]);

  useEffect(() => {
    if (refreshDate != null) {
      fetchSalesAndTransactions();
    }
  }, [refreshDate]);


  return (
    <Box>
      <DoubleBarChartCompared
        title={t('screens.dashboarding.charts.netSalesByTender')}
        currency={groupedAggregateTendersCompare?.currency}
        description={t('screens.dashboarding.charts.includingTax')}
        isError={isError}
        isLoading={isLoading}
        rightAxisLabel={t('screens.dashboarding.charts.volume')}
        leftAxisLabel={t('screens.dashboarding.charts.amount')}
        data={groupedAggregateTendersCompare?.tendersGroupedAggregate?.map(transformData) ?? []}
        dataCompared={groupedAggregateTendersCompare?.comparedTendersGroupedAggregate?.map(transformData) ?? []}
        bucket={params.bucket as Bucket}
        setBucket={value =>
          setParams(prev => ({
            ...prev,
            bucket: value as Bucket,
          }))
        }
        height={560}
        currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
        currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
        comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
        comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
        textLink={t('screens.dashboarding.charts.netSalesByTender')}
        linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SageReport.aspx`}
      />
    </Box>
  );
};

export default ComparedSalesByTender;
